import type { AIClient, GenerationOptions } from './clients';
import { OpenRouterClient } from './clients';
import { AiError } from './constants/ai-error';
import { AiException } from './exceptions/ai-exception';
import { GenerateMode } from './types/generate-mode-type';
import { DEFAULT_OPENROUTER_MODEL } from './types/model-type';

export interface AiConfig {
  apiKey?: string;
  model?: string;
  defaultOptions?: GenerationOptions;
  mode?: GenerateMode;
}

let aiConfig: AiConfig | null = null;
let aiClient: AIClient | null = null;

// Compares two AiConfig instances by the fields that actually drive client
// behavior (apiKey + model + mode). Used by `initAiModule` to
// allow idempotent re-init under dev HMR or test module re-imports — a
// second call with the SAME effective config is a no-op; a second call
// with a DIFFERENT effective config still throws `AiException(AI-02)`.
const isSameEffectiveConfig = (a: AiConfig, b: AiConfig): boolean =>
  a.apiKey === b.apiKey &&
  (a.model || DEFAULT_OPENROUTER_MODEL) ===
    (b.model || DEFAULT_OPENROUTER_MODEL) &&
  (a.mode ?? GenerateMode.sequential) === (b.mode ?? GenerateMode.sequential);

export function initAiModule(config: AiConfig): void {
  if (aiConfig) {
    if (isSameEffectiveConfig(aiConfig, config)) {
      // Already initialized with the same config — accept as no-op so
      // dev HMR / test re-imports don't crash the API boot path.
      return;
    }
    throw new AiException(AiError.ALREADY_INITIALIZED.defaultMessage, {
      code: AiError.ALREADY_INITIALIZED.code,
    });
  }

  aiConfig = {
    ...config,
    model: config.model || DEFAULT_OPENROUTER_MODEL,
    defaultOptions: config.defaultOptions || {
      maxTokens: 100,
      temperature: 0.7,
    },
    mode: config.mode || GenerateMode.sequential,
  };

  aiClient = createClient(aiConfig);
}

export function resetAiModule(): void {
  aiConfig = null;
  aiClient = null;
}

function createClient(config: AiConfig): AIClient {
  if (!config.apiKey) {
    throw new AiException(AiError.MISSING_API_KEY.defaultMessage, {
      code: AiError.MISSING_API_KEY.code,
    });
  }
  // biome-ignore lint/style/noNonNullAssertion: model defaulted in initAiModule
  return new OpenRouterClient(config.apiKey, config.model!);
}

export function getAiConfig(): AiConfig {
  if (!aiConfig) {
    throw new AiException(AiError.NOT_INITIALIZED.defaultMessage, {
      code: AiError.NOT_INITIALIZED.code,
    });
  }
  return aiConfig;
}

export function getAiClient(): AIClient {
  if (!aiClient) {
    throw new AiException(AiError.NOT_INITIALIZED.defaultMessage, {
      code: AiError.NOT_INITIALIZED.code,
    });
  }
  return aiClient;
}

export function getDefaultOptions(): GenerationOptions {
  if (!aiConfig?.defaultOptions) {
    throw new AiException(AiError.NOT_INITIALIZED.defaultMessage, {
      code: AiError.NOT_INITIALIZED.code,
    });
  }
  return aiConfig.defaultOptions;
}
